import {Collapse, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSetRecoilState } from "recoil";
import { getBookingAcrissList, getBookingCategories, getBookingLocations, getBookingSegments, getBookingSippList } from "../../../../api/services";
import { IBookingsApiFilter } from "../../../../api/types";
import { FormAutocomplete, FormDatePicker, FormDateTimePicker, FormNumberInput, FormTextField } from "../../../../components/fields";
import FilterActions from "../../../../components/filter/filter-actions/FilterActions";
import FilterWrapper from "../../../../components/filter/filter-wrapper/FilterWrapper";
import { ReactQueryKey } from "../../../../types";
import { LOCALE_DATE_FORMAT, LOCALE_ISO_DATE_TIME_FORMAT } from "../../../../utils";
import { pendingListFilterState } from "../../recoil";
import { IFormValuesPendingListFilter } from "../../types";
import TenantForm from "../../../../components/multitenant/TenantForm";
import DealerForm from "../../../../components/multitenant/DealerForm";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";

const FilterPendingList = () => {

    const { t } = useTranslation('booking');
    const { globalData } = useContext(GlobalContext);

    const setPendingListFilter = useSetRecoilState(pendingListFilterState);

    const validationSchema = yup.object({
        pickupDate: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable(),
        dropoffDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testDropoffDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dropoffDateTo ||
                        value <= context.parent.dropoffDateTo
                    );
                }
            ),
        dropoffDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testDropoffDateTo",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dropoffDateFrom ||
                        value >= context.parent.dropoffDateFrom
                    );
                }
            ),
        bookingDateFrom: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testBookingDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.bookingDateTo ||
                        value <= context.parent.bookingDateTo
                    );
                }
            ),
        bookingDateTo: yup
            .date()
            .typeError("message.validation.invalid_date")
            .nullable()
            .test(
                "testBookingDateFrom",
                "message.validation.invalid_date",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.bookingDateFrom ||
                        value >= context.parent.bookingDateFrom
                    );
                }
            ),
        tenants: yup
        .array()
        .min(1, "message.validation.required")
    });

    const form = useForm<IFormValuesPendingListFilter>({
        defaultValues: {
            bookingNumber: '',
            bookingDateFrom: null,
            bookingDateTo: null,
            categories: [],
            customerTypes: [],
            bookingOwnerFirstName: '',
            bookingOwnerLastName: '',
            bookingOwnerCompanyName: '',
            driverFirstName: '',
            driverLastName: '',
            pickupLocations: [],
            dropoffLocations: [],
            pickupDate: null,
            dropoffDateFrom: null,
            dropoffDateTo: null,
            rentalDaysFrom: 0,
            rentalDaysTo: 0,
            acrissList: [],
            sippList: [],
            tenants: globalData?.user.legalEntities?.map((elem: any) => elem.name),
            dealers: globalData?.user.dealers?.map((elem: any) => elem.name),
            keyless: ''
        },
        resolver: yupResolver(validationSchema, {}, { rawValues: true })
    });

    const tenantsWatch = useWatch<IFormValuesPendingListFilter, 'tenants'>({
        control: form.control,
        name: "tenants"
    });

    const dealersWatch = useWatch<IFormValuesPendingListFilter, 'dealers'>({
        control: form.control,
        name: "dealers"
    });

    const locationsQuery = useQuery(
        [ReactQueryKey.BookingLocations, { tenants: tenantsWatch, dealers: dealersWatch }],
        () => getBookingLocations({tenants:tenantsWatch, dealers:dealersWatch?? []}),
    );
    const acrissListQuery = useQuery(
        [ReactQueryKey.BookingAcrissList, { tenants: tenantsWatch, dealers: dealersWatch }],
        () => getBookingAcrissList({tenants:tenantsWatch, dealers:dealersWatch?? []}),
    );
    const sippListQuery = useQuery(
        [ReactQueryKey.BookingSippList, { tenants: tenantsWatch, dealersWatch }],
        () => getBookingSippList({tenants:tenantsWatch, dealers:dealersWatch?? []}),
    );
    const segmentsQuery = useQuery(
        [ReactQueryKey.BookingSegments, { tenants: tenantsWatch, dealersWatch }],
        () => getBookingSegments({tenants:tenantsWatch, dealers:dealersWatch?? []}),
    );
    const categoriesQuery = useQuery(
        [ReactQueryKey.BookingCategories, { tenants: tenantsWatch, dealersWatch }],
        () => getBookingCategories({tenants:tenantsWatch, dealers:dealersWatch?? []}),
    );

    const [expanded, setExpanded] = useState<boolean>(false);

    const createPendingListFilter = useCallback((data: IFormValuesPendingListFilter): IBookingsApiFilter => ({
        bookingNumber: data.bookingNumber || null,
        pickupLocations: data.pickupLocations.map(pl => pl.id),
        dropoffLocations: data.dropoffLocations.map(dl => dl.id),
        pickupDate: data.pickupDate?.format(LOCALE_DATE_FORMAT) || null,
        dropoffDateFrom: data.dropoffDateFrom?.format(LOCALE_ISO_DATE_TIME_FORMAT) || null,
        dropoffDateTo: data.dropoffDateTo?.format(LOCALE_ISO_DATE_TIME_FORMAT) || null,
        bookingDateFrom: data.bookingDateFrom?.toISOString() || null,
        bookingDateTo: data.bookingDateTo?.toISOString() || null,
        rentalDaysFrom: data.rentalDaysFrom || null,
        rentalDaysTo: data.rentalDaysTo || null,
        driverFirstName: data.driverFirstName || null,
        driverLastName: data.driverLastName || null,
        bookingOwnerFirstName: data.bookingOwnerFirstName || null,
        bookingOwnerLastName: data.bookingOwnerLastName || null,
        bookingOwnerCompanyName: data.bookingOwnerCompanyName || null,
        acrissList: data.acrissList.map(a => a),
        sippList: data.sippList.map(s => s),
        segments: data.customerTypes.map(s => s.code),
        categories: data.categories.map(p => p.code),
        tenants: data.tenants || [],
        dealers: data.dealers,
        keyless: data.keyless === '' ? undefined : Boolean(data.keyless)
    }), []);
    const handleSearch = useCallback((data: IFormValuesPendingListFilter) => {
        setPendingListFilter(createPendingListFilter(data));
        setExpanded(false);
    }, []);
    const handleReset = useCallback(() => {
        form.reset();
        setPendingListFilter(createPendingListFilter(form.getValues()));
        setExpanded(false);
    }, []);
    const handleToggle = useCallback(() => {
        setExpanded(current => !current);
    }, [setExpanded]);

    useEffect(() => {

        setPendingListFilter(createPendingListFilter(form.getValues()));
    }, [])

    return (
        <FilterWrapper>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleSearch)}>
                    <Grid container columnSpacing={5} alignItems="center">
                        <Grid item xs={12} sm={6} lg={3}>
                            <FormAutocomplete<IFormValuesPendingListFilter>
                                name="pickupLocations"
                                label={t('label.pickupLocations')}
                                options={locationsQuery.data?.locations ?? []}
                                loading={locationsQuery.isLoading}
                                multiple
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                getOptionLabel={(option) => option.label}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <FormDatePicker<IFormValuesPendingListFilter>
                                name="pickupDate"
                                label={t('label.pickupDate')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <FormAutocomplete<IFormValuesPendingListFilter>
                                name="categories"
                                label={t('label.categories')}
                                options={categoriesQuery.data ?? []}
                                multiple
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <FilterActions
                                expanded={expanded}
                                onSearch={form.handleSubmit(handleSearch)}
                                onReset={handleReset}
                                onToggle={handleToggle}
                            />
                        </Grid>
                    </Grid>
                    <Collapse in={expanded} timeout="auto" sx={{ width: '100%' }}>
                        <Grid container columnSpacing={5} rowSpacing={2} pt={2}>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<IFormValuesPendingListFilter>
                                    name="bookingNumber"
                                    label={t('label.bookingNumber')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<IFormValuesPendingListFilter>
                                    name="dropoffLocations"
                                    label={t('label.dropoffLocations')}
                                    options={locationsQuery.data?.locations ?? []}
                                    loading={locationsQuery.isLoading}
                                    multiple
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    getOptionLabel={(option) => option.label}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<IFormValuesPendingListFilter>
                                    name="dropoffDateFrom"
                                    label={t('label.dropoffDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<IFormValuesPendingListFilter>
                                    name="dropoffDateTo"
                                    label={t('label.dropoffDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<IFormValuesPendingListFilter>
                                    name="acrissList"
                                    label={t('label.acrissList')}
                                    options={acrissListQuery.data ?? []}
                                    multiple
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<IFormValuesPendingListFilter>
                                    name="sippList"
                                    label={t('label.sippList')}
                                    options={sippListQuery.data ?? []}
                                    multiple
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<IFormValuesPendingListFilter>
                                    name="driverFirstName"
                                    label={t('label.mainDriverFirstName')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<IFormValuesPendingListFilter>
                                    name="driverLastName"
                                    label={t('label.mainDriverLastName')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormNumberInput<IFormValuesPendingListFilter>
                                    name="rentalDaysFrom"
                                    label={t('label.rentalDaysFrom')}
                                    minValue={0}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormNumberInput<IFormValuesPendingListFilter>
                                    name="rentalDaysTo"
                                    label={t('label.rentalDaysTo')}
                                    minValue={0}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormAutocomplete<IFormValuesPendingListFilter>
                                    name="customerTypes"
                                    label={t('label.segments')}
                                    options={segmentsQuery.data ?? []}
                                    multiple
                                    isOptionEqualToValue={(option, value) => option.code === value.code}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<IFormValuesPendingListFilter>
                                    name="bookingOwnerCompanyName"
                                    label={t('label.bookingOwnerCompanyName')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<IFormValuesPendingListFilter>
                                    name="bookingOwnerFirstName"
                                    label={t('label.bookingOwnerFirstName')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormTextField<IFormValuesPendingListFilter>
                                    name="bookingOwnerLastName"
                                    label={t('label.bookingOwnerLastName')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<IFormValuesPendingListFilter>
                                    name="bookingDateFrom"
                                    label={t('label.creationDateFrom')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={3}>
                                <FormDateTimePicker<IFormValuesPendingListFilter>
                                    name="bookingDateTo"
                                    label={t('label.creationDateTo')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3} sx={{marginTop: "auto"}}>
                                <Controller
                                    control={form.control}
                                    name={"keyless"}
                                    render={({field}) => (
                                        <FormControl variant="standard" sx={{ width: '100%' }}>
                                            <InputLabel id="keyless-label">{t("label.keyless")}</InputLabel>
                                            <Select
                                                labelId="keyless-label"
                                                id="keyless-label"
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e);
                                                }}
                                            >
                                                <MenuItem value={''}></MenuItem>
                                                <MenuItem value={0}>{t("label.no")}</MenuItem>
                                                <MenuItem value={1}>{t("label.yes")}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                            {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) &&
                                <Grid item xs={12} md={6} lg={3}>
                                    <TenantForm
                                        control={form.control}
                                        name="tenants"
                                        onChange={() => form.setValue("dealers", [])}
                                    />
                                </Grid>
                            }
                            {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) &&
                                <Grid item xs={12} md={6} lg={3}>
                                    <DealerForm
                                        control={form.control}
                                        name="dealers"
                                        tenants={tenantsWatch}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </Collapse>
                </form>
            </FormProvider>
        </FilterWrapper>
    )
};

export default FilterPendingList;
